import { useState } from 'react'
import type { CharacterSpells } from '@shared/types'
import { CLASSES, type ClassKey } from '@shared/rules/constants'
import { api } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { SpellCard, levelLabel, type RulesSpell } from '@/components/rules/SpellCard'
import { Badge, Button, EmptyState, Modal, Panel, SectionHeading, Select } from '@/components/ui'

export function SpellsPanel({
  spells, className, canEdit, concentratingOn, onChange, onConcentrate,
}: {
  spells: CharacterSpells
  /** The character's class key, used to default the browser's filter. */
  className: string
  canEdit: boolean
  concentratingOn?: string | null
  onChange: (next: CharacterSpells) => void
  onConcentrate?: (spellId: string | null) => void
}) {
  const [browsing, setBrowsing] = useState(false)
  const known = [...spells.cantrips, ...spells.spells]
  const granted = [...spells.featCantrips, ...spells.featSpells]

  // One request for every id the character knows, rather than the whole list.
  const { data, loading } = useAsync(async () => {
    const all = await api.listSpells()
    return all.spells
  }, [])

  const byId = new Map((data ?? []).map((spell) => [spell.id, spell]))
  const knownSpells = known.map((id) => byId.get(id)).filter((s): s is RulesSpell => Boolean(s))
  const grantedSpells = granted.map((id) => byId.get(id)).filter((s): s is RulesSpell => Boolean(s))

  function toggle(spell: RulesSpell) {
    const key = spell.level === 0 ? 'cantrips' : 'spells'
    const list = spells[key]
    onChange({
      ...spells,
      [key]: list.includes(spell.id) ? list.filter((id) => id !== spell.id) : [...list, spell.id],
    })
  }

  if (loading) return null
  if (known.length === 0 && granted.length === 0 && !canEdit) return null

  return (
    <Panel className="p-4">
      <SectionHeading
        action={canEdit && <Button size="sm" onClick={() => setBrowsing(true)}>Add spells</Button>}
      >
        Spells
      </SectionHeading>

      {knownSpells.length === 0 && grantedSpells.length === 0 ? (
        <EmptyState
          title="No spells yet"
          description="Browse the spell list and pick the ones this character knows."
          action={canEdit ? <Button variant="primary" onClick={() => setBrowsing(true)}>Choose spells</Button> : undefined}
        />
      ) : (
        <div className="space-y-4">
          {knownSpells.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {knownSpells
                .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))
                .map((spell) => (
                  <SpellCard
                    key={spell.id}
                    spell={spell}
                    action={canEdit && (
                      <div className="flex gap-1.5">
                        {spell.concentration && onConcentrate && (
                          <button
                            type="button"
                            onClick={() => onConcentrate(concentratingOn === spell.id ? null : spell.id)}
                            aria-pressed={concentratingOn === spell.id}
                            title={concentratingOn === spell.id
                              ? 'Stop concentrating'
                              : concentratingOn ? 'Switch concentration to this spell' : 'Start concentrating'}
                            className={
                              'rounded-lg border px-2 py-1 text-xs transition-colors ' +
                              (concentratingOn === spell.id
                                ? 'border-gold-500 bg-gold-900/50 text-gold-400'
                                : 'border-ink-600 text-ink-400 hover:border-gold-600 hover:text-gold-400')
                            }
                          >
                            {concentratingOn === spell.id ? 'Concentrating' : 'Concentrate'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggle(spell)}
                          className="rounded-lg border border-ink-600 px-2 py-1 text-xs text-ink-500 transition-colors hover:border-blood-500/50 hover:text-blood-500"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  />
                ))}
            </div>
          )}

          {grantedSpells.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] uppercase tracking-wider text-ink-500">From a feat</p>
              <div className="flex flex-wrap gap-2">
                {grantedSpells.map((spell) => (
                  <Badge key={spell.id} tone="arcane">
                    {spell.name} · {levelLabel(spell.level)}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <SpellPicker
        open={browsing}
        onClose={() => setBrowsing(false)}
        defaultClass={className}
        known={known}
        onToggle={toggle}
      />
    </Panel>
  )
}

function SpellPicker({
  open, onClose, defaultClass, known, onToggle,
}: {
  open: boolean
  onClose: () => void
  defaultClass: string
  known: string[]
  onToggle: (spell: RulesSpell) => void
}) {
  const isCaster = Boolean(CLASSES[defaultClass as ClassKey]?.spellcastingAbility)
  const [klass, setKlass] = useState(isCaster ? defaultClass : 'all')
  const [level, setLevel] = useState('all')

  const { data, loading } = useAsync(
    () => (open ? api.listSpells({ class: klass, level }) : Promise.resolve({ spells: [] })),
    [open, klass, level],
  )

  return (
    <Modal open={open} title="Add spells" onClose={onClose}>
      <div className="space-y-3">
        <div className="flex gap-2">
          <Select value={klass} onChange={(e) => setKlass(e.target.value)} aria-label="Class list">
            <option value="all">Every class</option>
            {(Object.keys(CLASSES) as ClassKey[])
              .filter((key) => CLASSES[key].spellcastingAbility)
              .map((key) => <option key={key} value={key}>{CLASSES[key].name}</option>)}
          </Select>
          <Select value={level} onChange={(e) => setLevel(e.target.value)} aria-label="Spell level">
            <option value="all">Any level</option>
            <option value="0">Cantrips</option>
            <option value="1">1st level</option>
          </Select>
        </div>

        <div className="max-h-96 space-y-1.5 overflow-y-auto pr-1">
          {loading ? (
            <p className="py-6 text-center text-sm text-ink-500">Loading…</p>
          ) : (data?.spells ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-ink-500">Nothing matches those filters.</p>
          ) : (
            data!.spells.map((spell) => {
              const on = known.includes(spell.id)
              return (
                <button
                  key={spell.id}
                  type="button"
                  onClick={() => onToggle(spell)}
                  aria-pressed={on}
                  className={
                    'flex w-full items-center gap-2.5 rounded-xl border px-3 py-2 text-left transition-colors ' +
                    (on ? 'border-gold-500 bg-gold-900/30' : 'border-ink-700/70 hover:border-ink-500')
                  }
                >
                  <span className={
                    'grid size-4 shrink-0 place-items-center rounded border text-[10px] ' +
                    (on ? 'border-gold-500 bg-gold-500 text-on-accent' : 'border-ink-600 text-transparent')
                  }>✓</span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={'text-sm ' + (on ? 'text-gold-400' : 'text-ink-100')}>{spell.name}</span>
                      <span className="shrink-0 text-[10px] uppercase tracking-wider text-ink-500">
                        {levelLabel(spell.level)}
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-[12px] text-ink-400">{spell.description}</span>
                  </span>
                </button>
              )
            })
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  )
}
